// IndexedDB keystore for the NC1 identity — the browser twin of Windows'
// DPAPI keystore. Holds the UNWRAPPED identity secrets once the user has
// unlocked (at signup, via passkey PRF, device-transfer, or recovery code),
// so steady-state page loads decrypt without prompting.
//
// Same at-rest posture as Virgil e3kit's IndexedDB private-key store today.
// Note Account.vue's clearAllIndexedDB() (account deletion/reset) wipes this
// too — by design; the identity re-enters via passkey/transfer/recovery.

import localforage from 'localforage'

const store = localforage.createInstance({
  name: 'nc1-keystore',
})

const identityKey = (accountId) => `identity.${accountId}`

/**
 * secrets = { id_enc_pk, id_enc_sk, id_sign_pk, id_sign_sk, identity_version }
 * (the exact snake_case shape the §7.1 wraps protect). v3 (XWING.md §3.4):
 * `id_enc_pk` is the 1216-byte X-Wing public key and `id_enc_sk` its 32-byte
 * seed, both base64; the Ed25519 pair is unchanged.
 */
export async function saveIdentity(accountId, { secrets, altAccountId, email }) {
  await store.setItem(identityKey(accountId), {
    secrets,
    altAccountId,
    email,
    savedAt: Date.now(),
  })
}

export async function loadIdentity(accountId) {
  return await store.getItem(identityKey(accountId))
}

export async function clearIdentity(accountId) {
  await store.removeItem(identityKey(accountId))
}

// ---- local passkey marker ---------------------------------------------------
// WebAuthn has no silent "is one of these credentials usable here?" query, so
// at registration time we remember which credential THIS browser minted. Lets
// the UI say "a passkey protects this device ✓" instead of offering to mint a
// duplicate. Best-effort: cleared storage just means the UI offers Add again.

const passkeyKey = (accountId) => `passkey.${accountId}`

export async function recordLocalPasskey(accountId, credentialIdB64) {
  await store.setItem(passkeyKey(accountId), {
    credentialIdB64,
    registeredAt: Date.now(),
  })
}

export async function getLocalPasskey(accountId) {
  return await store.getItem(passkeyKey(accountId))
}

// ---- pending "Link a device" request ----------------------------------------
// The requester's one-time transfer key pair used to live in page memory only,
// so a reload, a closed tab or a phone freezing the tab killed the request
// while the approver still saw it, and approving it went nowhere
// (user-reported 2026-09-29). It now survives here, in the same store that
// holds the unwrapped identity once the link succeeds, until the grant
// arrives, the request is cancelled or rejected, or it ages past the server's
// 24-hour life. Shape: see DeviceLink.serializeLink.

const pendingLinkKey = (accountId) => `link.${String(accountId).toLowerCase()}`

export async function savePendingLink(accountId, link) {
  await store.setItem(pendingLinkKey(accountId), { ...link, savedAt: Date.now() })
}

export async function loadPendingLink(accountId) {
  return await store.getItem(pendingLinkKey(accountId))
}

export async function clearPendingLink(accountId) {
  await store.removeItem(pendingLinkKey(accountId))
}

// ---- invite links the OWNER created (INVITE-LINK-PASSKEY-FIRST.md §1.5) ----
// The link's fragment code exists nowhere else, so "Copy link again" on the
// recipients page needs this browser to have kept it. No new exposure: this
// store already holds the owner's identity, which opens everything the link
// can. Dropped when the invite is claimed, revoked or expired.

const inviteLinkKey = (ownerEmail, recipientEmail) =>
  `invite.${String(ownerEmail).toLowerCase()}.${String(recipientEmail).toLowerCase()}`

export async function saveInviteLink(ownerEmail, recipientEmail, link) {
  await store.setItem(inviteLinkKey(ownerEmail, recipientEmail), { ...link, savedAt: Date.now() })
}

export async function loadInviteLink(ownerEmail, recipientEmail) {
  return await store.getItem(inviteLinkKey(ownerEmail, recipientEmail))
}

export async function clearInviteLink(ownerEmail, recipientEmail) {
  await store.removeItem(inviteLinkKey(ownerEmail, recipientEmail))
}

// ---- explicit sign-out --------------------------------------------------------
// User decision 2026-10-13 (libsodium audit finding 8): an EXPLICIT sign-out
// forgets every secret this store holds - the unwrapped identity (for every
// account this browser held one for), a pending link request's transfer key,
// and the owner-minted invite links whose code exists nowhere else. The next
// sign-in unlocks again by passkey, recovery code or device link, none of
// which depend on anything here. Expired credentials, the idle timeout and
// the other automatic logouts keep everything (SignOut.js is the caller,
// lib/auth.js decides). Kept on purpose: the passkey markers (UI hints, not
// secrets); the pinned chain heads live in localStorage, not here, and stay.

const SECRET_PREFIXES = ['identity.', 'link.', 'invite.']

/** Is this store key one of the secret-bearing entries a sign-out forgets? */
export function isSecretEntry(key) {
  return typeof key === 'string' && SECRET_PREFIXES.some((p) => key.startsWith(p))
}

/** The pending link requests on file, [{ accountId, link }], for withdrawing them server-side first. */
export async function listPendingLinks() {
  const out = []
  for (const key of await store.keys()) {
    if (!key.startsWith('link.')) continue
    const link = await store.getItem(key)
    if (link) out.push({ accountId: key.slice('link.'.length), link })
  }
  return out
}

/** Remove every secret-bearing entry. Returns how many were removed. */
export async function clearSecretsForSignOut() {
  const keys = (await store.keys()).filter(isSecretEntry)
  await Promise.all(keys.map((k) => store.removeItem(k)))
  return keys.length
}
